import { defineStore } from 'pinia';
import { ref } from 'vue';

import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { RecipeStateKey, TripStateKey } from '@apps/main/modules/state/state';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';

import { CategoryType } from '@libs/trip-planner-interfaces';
import type {
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';

import { useTripOverviewPageStore } from '../../pages/tripOverview/stores';
import { useTripTypeSelectorStore } from '../../pages/tripOverview/components/tripTypeSelector/tripTypeSelectorStore';

export const useDestinationSelectorStore = defineStore(
  'DestinationSelectorStore',
  () => {
    const recipeState = injectStrict(RecipeStateKey);
    const tripState = injectStrict(TripStateKey);
    const tripOverviewPageStore = useTripOverviewPageStore();
    const tripTypeSelectorStore = useTripTypeSelectorStore();

    // State
    const searchText = ref<string>('');
    const autocompleteType = ref<DestinationAutocompleteType | undefined>(
      undefined
    );
    const editingIngredientId = ref<IngredientId | undefined>(undefined);

    // Actions
    function setDestinationSelectorData(
      newSearchText: string,
      ingredientId: IngredientId | undefined,
      modalType: DestinationAutocompleteType
    ): void {
      searchText.value = newSearchText;
      autocompleteType.value = modalType;
      editingIngredientId.value = ingredientId;
    }

    function handleConfirmSelection(
      newDestination: AutoCompleteSuggestion,
      isCarTrip?: boolean
    ): void {
      if (editingIngredientId.value) {
        updateDestination(newDestination, editingIngredientId.value);
      } else {
        addNewDestination(newDestination, isCarTrip);
      }
      clearDestinationSelectorData();
    }

    function handleOwnStayConfirmSelection(
      newDestination: AutoCompleteSuggestion,
      ownStayIngredientId: IngredientId
    ): void {
      tripOverviewPageStore.setDestinationTextToOwnStayIngredient(
        ownStayIngredientId,
        newDestination.label
      );
      clearDestinationSelectorData();
    }

    function changeDestinationByIngredientId(
      newDestination: AutoCompleteSuggestion,
      ingredientId: IngredientId
    ): void {
      updateDestination(newDestination, ingredientId);
    }

    function clearDestinationSelectorData(): void {
      searchText.value = '';
      autocompleteType.value = undefined;
      editingIngredientId.value = undefined;
    }

    function updateDestination(
      newDestination: AutoCompleteSuggestion,
      ingredientId: IngredientId
    ): void {
      const isHotelAutoCompleteSuggestion =
        newDestination.categoryType === CategoryType.Hotel;

      recipeState.updateHotelIngredientById(
        ingredientId,
        newDestination.label,
        newDestination.categoryType,
        {
          skipItinerarySearch: isHotelAutoCompleteSuggestion,
          forceItineraryLoad: isHotelAutoCompleteSuggestion,
        }
      );
    }

    function addNewDestination(
      newDestination: AutoCompleteSuggestion,
      isCarTrip?: boolean
    ): void {
      const isHotelAutoCompleteSuggestion =
        newDestination.categoryType === CategoryType.Hotel;

      const newHotelIngredient = new HotelIngredient(true);
      newHotelIngredient.setSearchText(
        newDestination.label,
        newDestination.categoryType
      );

      if (tripState.isEditingTripType.value) {
        tripTypeSelectorStore.setTripTypeOptions(newHotelIngredient);
      }

      if (isCarTrip) {
        recipeState.addCarTripIngredient(newHotelIngredient, {
          skipItinerarySearch: isHotelAutoCompleteSuggestion,
          forceItineraryLoad: isHotelAutoCompleteSuggestion,
        });
        return;
      }
      recipeState.addIngredient(newHotelIngredient, {
        skipItinerarySearch: isHotelAutoCompleteSuggestion,
        forceItineraryLoad: isHotelAutoCompleteSuggestion,
      });
    }

    function removeDestination(segment: EditTripSegment): void {
      recipeState.deleteIngredientById(segment.ingredientId);
    }

    return {
      // State
      searchText,
      autocompleteType,

      // Actions
      changeDestinationByIngredientId,
      clearDestinationSelectorData,
      handleConfirmSelection,
      handleOwnStayConfirmSelection,
      setDestinationSelectorData,
      removeDestination,
    };
  }
);
